/**
 * ConfirmOverwriteModal - Modal for resolving meal scheduling conflicts
 *
 * This component allows users to choose how to handle meal overlaps
 * when rescheduling or repeating meals in the meal plan editor.
 */

import React, { useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-modal';

import { useOverlapResolution } from '../../hooks/useOverlapResolution';
import OverlapDisplay from './OverlapDisplay.react';
import { useModalControls } from '../../contexts/ModalContext';
import { useMealOperations } from '../../contexts/MealOperationsContext';
import { useMealPlanData } from '../../contexts/MealPlanContext';
import { MODAL_TYPES } from '../../hooks/useModalManager';
import { AssetsProvider } from '../../contexts';
import { getAssetsForMeals } from '@utils/Meals';

const ConfirmOverwriteModal = ({ closeModal }) => {
    const { getModalData } = useModalControls();
    const mealOperations = useMealOperations();
    const { mealTypes } = useMealPlanData();

    // State for loaded assets and loading flag
    const [assets, setAssets] = useState({ recipes: {}, foods: {} });
    const [loading, setLoading] = useState(true);

    // Get all overlap data from context via getModalData
    const overlapData = getModalData(MODAL_TYPES.OVERLAP_RESOLUTION);
    const {
        overlaps,
        meals = [],
        clears = [],
        targetOffset: offset,
        targetMealType: mealType,
        isRepeatMeal = false,
        returnMode = false,
        isRecipeSearch = false,
        onReturnConflictData,
        applyOverlapResolution: overlapApplyFunction,
        pendingLeftovers,
        additionalMealToAdd,
        additionalMealsToDelete
    } = overlapData || {};

    // Use overlap-specific function if provided, otherwise fall back to meal operations
    const applyOverlapResolution = overlapApplyFunction || mealOperations.applyOverlapResolution;

    const mode = isRepeatMeal ? 'repeat' : 'reschedule';

    // Safety check - if no overlap data, don't render
    if (!overlapData || !overlaps || overlaps.length === 0) {
        return null;
    }

    // Load assets for all conflict meals (mirroring legacy modal pattern)
    useEffect(() => {
        let isMounted = true;

        const loadAssets = async () => {
            if (!overlaps || overlaps.length === 0) {
                if (isMounted) setLoading(false);
                return;
            }

            try {
                // Collect all meals from overlaps (same as legacy modal)
                const allMeals = overlaps.reduce((carry, overlap) => {
                    return carry.concat(overlap.destMeals || []).concat(overlap.sourceMeals || []);
                }, []);

                const fetchedAssets = await getAssetsForMeals(allMeals);

                if (isMounted) {
                    setAssets(fetchedAssets);
                    setLoading(false);
                }
            } catch (error) {
                console.error('Failed to load meal assets:', error);
                if (isMounted) setLoading(false);
            }
        };

        loadAssets();

        return () => {
            isMounted = false;
        };
    }, [overlaps]);

    // Use the overlap resolution hook
    const {
        sortedOverlaps,
        overlapActions,
        canResolve,
        updateOverlapAction,
        resolveAndComplete
    } = useOverlapResolution({
        overlaps,
        meals,
        mode,
        clears,
        targetOffset: offset,
        targetMealType: mealType,
        mealTypes,
        returnMode,
        isRecipeSearch,
        onComplete: (resolution) => {
            // Return mode: send conflict resolution data back instead of applying directly
            if (returnMode && onReturnConflictData) {
                // Process overlaps to create conflict resolutions data similar to ConfirmOverwriteModal
                // Use overlapActions to get the user's selected actions, not the original overlaps
                const conflictResolutions = overlapActions.map((overlapAction, index) => ({
                    conflictId: index,
                    action: overlapAction.action || 'replace',
                    overlap: overlapAction,
                    conflictingMeals: overlapAction.destMeals
                }));

                onReturnConflictData(conflictResolutions);
                closeModal(false); // Don't close all modals
                return;
            }

            // Normal mode: apply overlap resolution directly
            const { mealsToUpdate, mealsToDelete: resolvedMealsToDelete, leftoverOffsets } = resolution;

            // Convert update format for applyOverlapResolution
            let dirtyMeals = mealsToUpdate.map(update => ({
                ...meals.find(m => (m.uuid || m.id) === update.uuid) ||
                   overlaps.flatMap(o => [...o.destMeals, ...o.sourceMeals])
                          .find(m => (m.uuid || m.id) === update.uuid),
                ...clears.flatMap(o => [...o.sourceMeals])
                    .find(m => (m.uuid || m.id) === update.uuid),
                ...update.updates
            }));

            // Add additional meal if provided (for swap flow main meal)
            if (additionalMealToAdd) {
                dirtyMeals = [additionalMealToAdd, ...dirtyMeals];
            }

            // Add additional meals to delete if provided (for swap flow replaceMeals)
            let mealsToDelete = resolvedMealsToDelete;
            if (additionalMealsToDelete && additionalMealsToDelete.length > 0) {
                mealsToDelete = [...mealsToDelete, ...additionalMealsToDelete];
            }

            // Apply overlap resolution using CRUD operations
            const result = applyOverlapResolution(dirtyMeals, mealsToDelete, leftoverOffsets);
            if (result?.success) {
                closeModal(true);
            }
        }
    });

    return (
        <Modal
            isOpen={true}
            onRequestClose={closeModal}
            contentLabel="Resolve meal conflicts"
            className="el-modal el-modal2"
            overlayClassName="el-modal-overlay"
            closeTimeoutMS={250}
        >
            <AssetsProvider assets={assets}>
                <section className="leftover-offsets-container confirm-overwrite-container-v2 el-modal-container el-modal2-container">
                    <header>
                        <h2>What should we do with your previously scheduled {overlaps.length > 1 ? 'meals' : 'meal'}?</h2>

                        <button className="el-modal-close-x" onClick={closeModal}>
                            <i className="icon-close-x" />
                            <span className="assistive-text">Close Modal</span>
                        </button>
                    </header>

                    <div className="el-modal-body-container el-modal2-body-container confirm-overwrite-form">
                        {loading ? (
                            <div className="el-modal-loading">
                                <i className="icon-spinner2" />
                            </div>
                        ) : (
                            <div className="overlap-list">
                                {sortedOverlaps.map((overlap) => (
                                    <div key={overlap.originalIndex} className="overlap-item">
                                        <OverlapDisplay
                                            overlap={overlap}
                                            action={overlap.action}
                                            onActionChange={(action) => updateOverlapAction(overlap.originalIndex, action)}
                                            mode={mode}
                                        />
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <footer>
                        <button className="el-modal-cancel-btn" onClick={closeModal}>Cancel</button>
                        <button
                            className="el-modal-ok-btn"
                            onClick={resolveAndComplete}
                            disabled={!canResolve}
                        >
                            Save
                        </button>
                    </footer>
                </section>
            </AssetsProvider>
        </Modal>
    );
};

ConfirmOverwriteModal.propTypes = {
    closeModal: PropTypes.func.isRequired
};

export default ConfirmOverwriteModal;
